/* eslint-disable @typescript-eslint/no-explicit-any */
import * as XLSX from "xlsx";

/**
 * Helpers for exporting Qlik hypercube data to Excel with native data types.
 *
 * Qlik returns every cell with both a formatted string (`qText`) and the raw
 * numeric value (`qNum`). Exporting `qText` alone makes Excel treat amounts and
 * dates as text ("Number Stored as Text"), breaking SUM, sorting and pivots.
 * These helpers map each cell to a typed XLSX cell, using the column's Qlik
 * number-format metadata to decide between date, numeric and text. See
 * SIMS-5661.
 */

export interface QlikColumnMeta {
	isMeasure: boolean;
	qType?: string;
	qFmt?: string;
	qnDec?: number;
}

/**
 * Build the natural-order (dimensions then measures) column metadata array for
 * a hypercube layout. The order matches the raw `qMatrix` column order.
 */
export const extractColumnMeta = (layout: any): QlikColumnMeta[] => {
	const toMeta =
		(isMeasure: boolean) =>
		(info: any): QlikColumnMeta => {
			const nf = info?.qNumFormat || {};
			return {
				isMeasure,
				qType: nf.qType,
				qFmt: nf.qFmt,
				qnDec: nf.qnDec,
			};
		};

	const dims = (layout?.qHyperCube?.qDimensionInfo || []).map(toMeta(false));
	const meas = (layout?.qHyperCube?.qMeasureInfo || []).map(toMeta(true));
	return [...dims, ...meas];
};

// Qlik uses uppercase tokens (YYYY/MM/DD) while Excel format codes are
// lowercase. Lowercasing is enough for the date/timestamp patterns Qlik emits;
// Excel disambiguates month vs. minute by position.
const qlikDateFmtToExcel = (qFmt?: string, qType?: string): string => {
	if (qFmt && qFmt.trim() !== "") return qFmt.toLowerCase();
	if (qType === "T") return "hh:mm:ss";
	if (qType === "TS") return "mm/dd/yyyy hh:mm:ss";
	return "mm/dd/yyyy";
};

const numberFmt = (meta: QlikColumnMeta): string => {
	const dec =
		typeof meta.qnDec === "number" && isFinite(meta.qnDec)
			? Math.min(Math.max(Math.trunc(meta.qnDec), 0), 4)
			: 2;
	return dec <= 0 ? "#,##0" : `#,##0.${"0".repeat(dec)}`;
};

/**
 * Convert a single Qlik hypercube cell to a typed XLSX cell.
 *
 * - Date / time dimensions -> numeric cell with a date format (Qlik date serials
 *   share Excel's epoch, so `qNum` is directly usable).
 * - Measures with a usable numeric value -> numeric cell, currency-formatted
 *   when the column carries a money format.
 * - Everything else (names, IDs, NPI, CIN, status) -> text cell.
 */
export const toTypedCell = (
	cell: any,
	meta: QlikColumnMeta | undefined,
	currencyMask?: string,
): XLSX.CellObject => {
	const text = cell?.qText != null ? String(cell.qText) : "";
	const num = cell?.qNum;
	const hasNum = typeof num === "number" && isFinite(num);
	const qType = meta?.qType;

	if ((qType === "D" || qType === "T" || qType === "TS") && hasNum) {
		return { t: "n", v: num, z: qlikDateFmtToExcel(meta?.qFmt, qType) };
	}

	if (meta?.isMeasure && hasNum) {
		const moneyLike =
			qType === "M" || (!!meta?.qFmt && /[^0#.,\-\s]/.test(meta.qFmt));
		const z = moneyLike ? `"${currencyMask ?? ""}"#,##0.00` : numberFmt(meta);
		return { t: "n", v: num, z };
	}

	return { t: "s", v: text };
};

/**
 * Assemble a worksheet from a string header row and a matrix of typed cells.
 * Cells are placed directly (rather than via `aoa_to_sheet`) so their `t`/`z`
 * type and format information is preserved.
 */
export const buildExcelSheet = (
	headers: string[],
	cellRows: XLSX.CellObject[][],
): XLSX.WorkSheet => {
	const ws: XLSX.WorkSheet = {};

	headers.forEach((h, c) => {
		ws[XLSX.utils.encode_cell({ r: 0, c })] = { t: "s", v: h };
	});

	cellRows.forEach((row, ri) => {
		row.forEach((cell, c) => {
			ws[XLSX.utils.encode_cell({ r: ri + 1, c })] = cell;
		});
	});

	const colCount = Math.max(
		headers.length,
		...cellRows.map((r) => r.length),
		1,
	);
	ws["!ref"] = XLSX.utils.encode_range({
		s: { r: 0, c: 0 },
		e: { r: cellRows.length, c: colCount - 1 },
	});
	ws["!cols"] = Array.from({ length: colCount }, () => ({ wch: 20 }));

	return ws;
};
